import type { ReactNode } from "react";
import { FlaskConical } from "lucide-react";
import type {
  BusinessStatus,
  CampaignStatus,
  CompanyEmailStatus,
  ContactEmailStatus,
  EmailScope,
  ProviderMode,
} from "@/api/email";
import {
  CAMPAIGN_STATUS,
  COMPANY_STATUS,
  CONTACT_STATUS,
  PROVIDER_MODE,
  SCOPE,
  type Tone,
} from "@/lib/email";
import { getStatusColor } from "@/lib/constants";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

/** A short explanation on hover, for badges and icons. */
export function Hint({
  content,
  children,
  side = "top",
}: {
  content: ReactNode;
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
}) {
  if (!content) return <>{children}</>;
  return (
    <TooltipProvider delayDuration={250}>
      <Tooltip>
        <TooltipTrigger asChild>{children}</TooltipTrigger>
        <TooltipContent side={side} className="max-w-xs bg-slate-900 text-[12px] leading-snug">
          {content}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}

export function TonePill({
  tone,
  className,
  hint,
  children,
}: {
  tone: Tone;
  className?: string;
  hint?: ReactNode;
  children?: ReactNode;
}) {
  const pill = (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium",
        tone.pill,
        className,
      )}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", tone.dot)} />
      <span className="truncate">{children ?? tone.label}</span>
    </span>
  );
  const content = hint ?? tone.hint;
  return content ? <Hint content={content}>{pill}</Hint> : pill;
}

export function ContactStatusBadge({
  status,
  reason,
  className,
}: {
  status: ContactEmailStatus;
  reason?: string | null;
  className?: string;
}) {
  const tone = CONTACT_STATUS[status];
  return (
    <TonePill
      tone={tone}
      className={className}
      hint={
        reason ? (
          <span>
            {tone.hint}
            <span className="mt-1 block font-medium text-white">{reason}</span>
          </span>
        ) : undefined
      }
    />
  );
}

export function CompanyEmailBadge({
  status,
  className,
}: {
  status: CompanyEmailStatus | null;
  className?: string;
}) {
  if (!status) {
    return <span className={cn("text-xs text-slate-400", className)}>No email yet</span>;
  }
  return <TonePill tone={COMPANY_STATUS[status]} className={className} />;
}

export function CampaignStatusBadge({ status }: { status: CampaignStatus }) {
  const tone = CAMPAIGN_STATUS[status];
  return (
    <TonePill tone={tone}>
      {status === "ACTIVE" ? (
        <span className="inline-flex items-center gap-1">{tone.label}</span>
      ) : undefined}
    </TonePill>
  );
}

export function ScopeBadge({ scope, className }: { scope: EmailScope; className?: string }) {
  const tone = SCOPE[scope];
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-md border px-1.5 py-px text-[11px] font-semibold tracking-wide",
        tone.pill,
        className,
      )}
    >
      {tone.label}
    </span>
  );
}

export function ProviderModeBadge({ mode }: { mode: ProviderMode }) {
  const tone = PROVIDER_MODE[mode];
  if (mode === "DRY_RUN") {
    return (
      <Hint content={tone.hint}>
        <span
          className={cn(
            "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium",
            tone.pill,
          )}
        >
          <FlaskConical className="h-3 w-3" />
          {tone.label}
        </span>
      </Hint>
    );
  }
  return <TonePill tone={tone} />;
}

/** The company's pipeline status, colored as the project's settings say. */
export function BusinessStatusBadge({
  status,
  label,
  statuses,
}: {
  status: string | null;
  label: string | null;
  statuses?: BusinessStatus[];
}) {
  if (!status) return <span className="text-xs text-slate-400">—</span>;
  const colors = getStatusColor(
    status,
    statuses?.map((s) => ({ key: s.key, color: s.color ?? undefined })),
  );
  return (
    <span
      className={cn(
        "inline-flex max-w-full items-center gap-1.5 whitespace-nowrap rounded-full border px-2 py-0.5 text-xs font-medium",
        colors.bg,
        colors.text,
        colors.border,
      )}
    >
      <span className={cn("h-1.5 w-1.5 shrink-0 rounded-full", colors.dot)} />
      <span className="truncate">{label ?? status}</span>
    </span>
  );
}

/** Says, wherever it matters, that nothing leaves Prism. */
export function DryRunNotice({ className, compact }: { className?: string; compact?: boolean }) {
  if (compact) {
    return (
      <Hint content="EMAIL_PROVIDER_EXTERNAL_EFFECTS is off: syncs are simulated and nothing reaches Lemlist.">
        <span
          className={cn(
            "inline-flex items-center gap-1.5 rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-xs font-medium text-amber-800",
            className,
          )}
        >
          <FlaskConical className="h-3.5 w-3.5" />
          Dry run — nothing is sent
        </span>
      </Hint>
    );
  }
  return (
    <div
      className={cn(
        "flex items-start gap-3 rounded-xl border border-amber-200/80 bg-amber-50/70 px-4 py-3 text-sm text-amber-900",
        className,
      )}
    >
      <FlaskConical className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
      <p>
        <span className="font-semibold">Dry run.</span> External effects are off: syncing
        simulates Lemlist and nothing is sent to anyone. Campaigns, steps and contacts are
        complete on the Prism side and ready for a real sync later.
      </p>
    </div>
  );
}
